import axios from "axios";
import { auth, firestore } from "containers/firebase";
import { COLLECTION } from "shared/strings/firebase";
import { firestoreToArray } from "./helpers";
import batchUtils from "utils/batchUtils";
import { excludeDeletedUsers, isDeletedUserRecord } from "utils/userFilters";
// Use modular Firestore APIs for aggregation queries (counts)
import { getFirestore, collection as fsCollection, query as fsQuery, where as fsWhere, getCountFromServer } from 'firebase/firestore';

const VITE_CLOUD_FUNCTION_API_URL = import.meta.env.VITE_CLOUD_FUNCTION_API_URL;

// --- CACHE LOGIC ---
const memoryCache = {};
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
const LOCAL_STORAGE_KEY = "gpwebapp_cache";

function getCacheStore() {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

function saveCacheStore(store) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(store));
}

export function getCacheKey(functionName, params) {
  return functionName + ':' + JSON.stringify(params);
}

export const countAllUsers = async (cityId) => {
  try {
    // Use modular Firestore for aggregation count to ensure compatibility
    const db = getFirestore();
    const usersQuery = fsQuery(
      fsCollection(db, COLLECTION.Users),
      fsWhere("cityId", "==", cityId)
    );
    const snapshot = await getCountFromServer(usersQuery);
    return snapshot.data().count || 0;
  } catch (error) {
    console.error('Error counting all users:', error);
    return 0;
  }
};

export const countAllOrganisations = async (cityId) => {
  try {
    // Use modular Firestore for aggregation count to ensure compatibility
    const db = getFirestore();
    const organisationsQuery = fsQuery(
      fsCollection(db, COLLECTION.Organisations),
      fsWhere("cityId", "==", cityId)
    );
    const snapshot = await getCountFromServer(organisationsQuery);
    return snapshot.data().count || 0;
  } catch (error) {
    console.error('Error counting all organisations:', error);
    return 0;
  }
};

export function getFromCache(key) {
  const store = getCacheStore();
  const entry = store[key];
  if (entry && (Date.now() - entry.timestamp < CACHE_DURATION)) {
    return entry.value;
  }
  return null;
}

export function setCache(key, value) {
  const store = getCacheStore();
  store[key] = { value, timestamp: Date.now() };
  saveCacheStore(store);
}

export function clearApiCache() {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch (error) {
    console.warn("Unable to clear API cache:", error);
  }
}

// common for anything
export const generateFirestoreId = () => {
  return firestore.collection('generateFirestoreId').doc().id;
}

// common for cities and organisations
export const getCompletelyStats = async (params) => {
  if (params.period == null) return;
  const cacheKey = getCacheKey('getCompletelyStats', params);
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/v2/get-stats-by?ownerType=${params.ownerType}&ownerId=${params.ownerId}&period=${params.period}&periodKey=${params.periodKey}&branchId=${params.branchId}&withAllUsers=${params.withAllUsers}&startDate=${params.startDate}&endDate=${params.endDate}`
    )
    .then((res) => res.data)
    .catch((err) => { console.log(`error getCompletelyStats : ${err}`) });

  setCache(cacheKey, res);
  return res;
};

export const getUsersActivities = async (params) => {
  if (params.period == null) return;
  const cacheKey = getCacheKey('getUsersActivities', params);
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/v2/get-useractivities-stats-by?ownerType=${params.ownerType}&ownerId=${params.ownerId}&period=${params.period}&periodKey=${params.periodKey}&branchId=${params.branchId}&withAllUsers=${params.withAllUsers}&startDate=${params.startDate}&endDate=${params.endDate}`
    )
    .then((res) => res.data)
    .catch((err) => { console.log(`error get-useractivities-stats-by : ${err}`) });

  setCache(cacheKey, res);
  return res;
};

export const getCityAllOrganisations = (cityId) => {
  // Pas de cache ici car accès Firestore direct (peut être ajouté si besoin)
  return firestore.collection(COLLECTION.Organisations)
    .where('cityId', '==', cityId)
    .get()
    .then(firestoreToArray);
};

export const onceUpdateAllDocumentsInCollection = async (collectionName, data) => {
  // Pas de cache ici, opération d'écriture
  if (!collectionName || !data) {
    throw new Error(`Invalid collectionName: ${collectionName} or data: ${data}`);
  }
  await firestore
    .collection(collectionName)
    .get()
    .then(async (snapshot) => {
      const docsWitId = firestoreToArray(snapshot);;
      return batchUtils.batchLimitParallel({
        firestore: firestore,
        items: docsWitId,
        limit: 250,
        onEach: async (doc, batch) => {
          const docRef = firestore.collection(collectionName).doc(doc.id);
          batch.update(docRef, data);
        },
      });
    });
};

export const fetchDashboardSustainableDistance = async (params) => {
  const cacheKey = getCacheKey('fetchDashboardSustainableDistance', params);
  const cached = getFromCache(cacheKey);

  if (cached) return cached;
  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-total-sustainable-distance?ownerType=${params.ownerType}&ownerId=${params.ownerId}&branchId=${params.branchId}&startDate=${params.startDate}&endDate=${params.endDate}&challenge=${params.challengeId || ''}`
    )
    .then((res) => res.data)
    .catch((err) => { console.log(`error get-total-sustainable-distance : ${err}`) });

  setCache(cacheKey, res);
  return res;
};

export const fetchDashboardGHG = async (params) => {
  const cacheKey = getCacheKey('fetchDashboardGHG', params);
  const cached = getFromCache(cacheKey);

  if (cached) return cached;
  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-total-ghg?ownerType=${params.ownerType}&ownerId=${params.ownerId}&branchId=${params.branchId}&startDate=${params.startDate}&endDate=${params.endDate}&challenge=${params.challengeId || ''}`
    )
    .then((res) => res.data)
    .catch((err) => { console.log(`error get-total-ghg : ${err}`) });

  setCache(cacheKey, res);
  return res;
};

export const fetchOrganisationScores = async (params) => {
  const cacheKey = getCacheKey('fetchOrganisationScores1', params);
  const cached = getFromCache(cacheKey);

  if (cached) return cached;
  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-organisations-scores?ownerType=${params.ownerType}&ownerId=${params.ownerId}&challenge=${params.challengeId || ''}`
    )
    .then((res) => res.data)
    .catch((err) => { console.log(`error get-organisations-scores : ${err}`) });

  setCache(cacheKey, res);

  return res;
};

export const fetchInterCompanyChallengeDuel = async (params) => {
  const today = new Date().toLocaleDateString('en-CA');
  const cacheKey = getCacheKey('fetchInterCompanyChallengeDuel', { ...params, cacheDate: today });
  const cached = getFromCache(cacheKey);
  // if (cached) return cached;

  const queryParams = new URLSearchParams({
    ownerType: params.ownerType,
    ownerId: params.ownerId,
    challengeId: params.challengeId,
  });

  if (params.branchId) {
    queryParams.set('branchId', params.branchId);
  }

  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error("Missing bearer token");
  }

  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-inter-company-challenge-duel?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    .then((response) => response.data);

  setCache(cacheKey, res);
  return res;
};

export const fetchChallengeInfoStats = async (params) => {
  const cacheKey = getCacheKey('fetchChallengeInfoStats', params);
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const queryParams = new URLSearchParams({
    ownerType: params.ownerType,
    ownerId: params.ownerId,
    challenge: params.challengeId || '',
    startDate: params.startDate,
    endDate: params.endDate,
  });

  if (params.branchId) {
    queryParams.set('branchId', params.branchId);
  }

  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-challenge-info-stats?${queryParams.toString()}`
    )
    .then((response) => response.data)
    .catch((err) => { console.log(`error get-challenge-info-stats : ${err}`) });

  setCache(cacheKey, res);
  return res;
};

export const fetchDashboardSustainableSessions = async (params) => {
  const cacheKey = getCacheKey('fetchDashboardSustainableSessions', params);
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-total-sustainable-sessions?ownerType=${params.ownerType}&ownerId=${params.ownerId}&branchId=${params.branchId}&startDate=${params.startDate}&endDate=${params.endDate}&challenge=${params.challengeId || ''}`
    )
    .then((res) => res.data)
    .catch((err) => { console.log(`error get-total-sustainable-sessions : ${err}`) });

  setCache(cacheKey, res);
  return res;
};

export const fetchDashboardTotalGreenpoints = async (params) => {
  const cacheKey = getCacheKey('fetchDashboardTotalGreenpoints', params);
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-total-greenpoints?ownerType=${params.ownerType}&ownerId=${params.ownerId}&branchId=${params.branchId}&startDate=${params.startDate}&endDate=${params.endDate}&challenge=${params.challengeId || ''}`
    )
    .then((res) => res.data)
    .catch((err) => { console.log(`error get-total-greenpoints : ${err}`) });

  setCache(cacheKey, res);
  return res;
};

export const fetchDashboardAllGreenpoints = async (params) => {
  const cacheKey = getCacheKey('fetchDashboardAllGreenpoints', params);
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-all-greenpoints?ownerType=${params.ownerType}&ownerId=${params.ownerId}&branchId=${params.branchId}&startDate=${params.startDate}&endDate=${params.endDate}&challenge=${params.challengeId || ''}`
    )
    .then((res) => res.data)
    .catch((err) => { console.log(`error get-all-greenpoints : ${err}`) });

  setCache(cacheKey, res);
  return res;
};

export const fetchDashboardActiveUsersCount = async (params) => {
  const cacheKey = getCacheKey('fetchDashboardActiveUsersCount', params);
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-total-active-users-count?ownerType=${params.ownerType}&ownerId=${params.ownerId}&branchId=${params.branchId}&startDate=${params.startDate}&endDate=${params.endDate}&challenge=${params.challengeId || ''}`
    )
    .then((res) => res.data)
    .catch((err) => { console.log(`error get-total-active-users-count : ${err}`) });

  setCache(cacheKey, res);
  return res;
};

export const fetchDashboardTotalActivities = async (params) => {
  const cacheKey = getCacheKey('fetchDashboardTotalActivities', params);
  const cached = getFromCache(cacheKey);
  if (cached) return cached;
  const url = `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-total-activities?ownerType=${params.ownerType}&ownerId=${params.ownerId}&branchId=${params.branchId}&startDate=${params.startDate}&endDate=${params.endDate}&challenge=${params.challengeId || ''}`
  const res = await axios
    .get(url)
    .then((res) => res.data)
    .catch((err) => { console.log(`error get-total-activities : ${err}`) });

  setCache(cacheKey, res);
  return res;
};

export const fetchDashboardTotalPeriods = async (params) => {
  const cacheKey = getCacheKey('fetchDashboardTotalPeriods', params);
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-total-periods?ownerType=${params.ownerType}&ownerId=${params.ownerId}&branchId=${params.branchId}&startDate=${params.startDate}&endDate=${params.endDate}&challenge=${params.challengeId || ''}`
    )
    .then((res) => res.data)
    .catch((err) => { console.log(`error get-total-periods : ${err}`) });

  setCache(cacheKey, res);
  return res;
};

export const fetchDashboardTotalUsers = async (params) => {
  const { page = 1, limit = 10, sortBy = '', sortOrder = 'asc', search = '' } = params;

  // Include pagination and sorting params in cache key for server-side pagination/sorting
  const cacheKey = getCacheKey('fetchDashboardTotalUsers', { ...params, page, limit, sortBy, sortOrder });
  const cached = getFromCache(cacheKey);
  if (cached) return cached;
  const queryParams = new URLSearchParams({
    ownerType: params.ownerType,
    ownerId: params.ownerId,
    branchId: params.branchId,
    startDate: params.startDate,
    endDate: params.endDate,
    challenge: params.challengeId || '',
    page: String(page),
    limit: String(limit),
    sortBy,
    sortOrder,
  });

  if (search) {
    queryParams.set('search', search);
  }

  const totalUsersUrl = `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-total-users-new?${queryParams.toString()}`;

  console.log('VITE_CLOUD_FUNCTION_API_URL', totalUsersUrl);
  const res = await axios

    .get(
      //<<<<<<< HEAD
      totalUsersUrl
      // =======
      //       `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-total-users-new?ownerType=${params.ownerType}&ownerId=${params.ownerId}&branchId=${params.branchId}&startDate=${params.startDate}&endDate=${params.endDate}&challenge=${params.challengeId || ''}`

      //`${VITE_CLOUD_FUNCTION_API_URL}/statsApi/get-total-users-new-legacy?ownerType=${params.ownerType}&ownerId=${params.ownerId}&branchId=${params.branchId}&startDate=${params.startDate}&endDate=${params.endDate}&challenge=${params.challengeId || ''}`
      //>>>>>>> DSA
    )
    .then((res) => res.data)
    .catch((err) => { console.log(`error get-total-users-new : ${err}`) });

  setCache(cacheKey, res);
  return res;
};

export const getUsersBasic = async (params = {}) => {
  const { page = 1, limit = 10, search = "", cityId = "", organisationId = "", branchId = "", region = "", regions = [], postalCodePrefix = "", selectedRange = {} } = params;
  const normalizedRegions = Array.isArray(regions) && regions.length > 0
    ? regions
    : (region ? region.split(",").map((entry) => entry.trim()).filter(Boolean) : []);
  const cacheKey = getCacheKey("getUsersBasic", {
    page,
    limit,
    search,
    selectedRange,
    cityId,
    organisationId,
    branchId,
    regions: normalizedRegions,
    postalCodePrefix,
  });
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const queryParams = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (search) {
    queryParams.set("search", search);
  }

  if (cityId) {
    queryParams.set("cityId", cityId);
  }

  if (organisationId) {
    queryParams.set("organisationId", organisationId);
  }

  if (branchId) {
    queryParams.set("branchId", branchId);
  }

  if (normalizedRegions.length > 0) {
    queryParams.set("region", normalizedRegions.join(","));
  }

  if (postalCodePrefix) {
    queryParams.set("postalCodePrefix", postalCodePrefix);
  }

  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/v2/get-users-basic?${queryParams.toString()}`
    )
    .then((response) => response.data)
    .catch((err) => {
      console.log(`error get-users-basic : ${err}`);
      return null;
    });

  setCache(cacheKey, res);
  return res;
};

export const getOrganisationsBasic = async (params = {}) => {
  const { page = 1, limit = 100, search = "", cityId = "", region = "", regions = [], postalCodePrefix = "" } = params;
  const normalizedRegions = Array.isArray(regions) && regions.length > 0
    ? regions
    : (region ? region.split(",").map((entry) => entry.trim()).filter(Boolean) : []);
  const cacheKey = getCacheKey("getOrganisationsBasic", {
    page,
    limit,
    search,
    cityId,
    regions: normalizedRegions,
    postalCodePrefix,
  });
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const queryParams = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (search) {
    queryParams.set("search", search);
  }

  if (cityId) {
    queryParams.set("cityId", cityId);
  }

  if (normalizedRegions.length > 0) {
    queryParams.set("region", normalizedRegions.join(","));
  }

  if (postalCodePrefix) {
    queryParams.set("postalCodePrefix", postalCodePrefix);
  }

  const res = await axios
    .get(
      `${VITE_CLOUD_FUNCTION_API_URL}/statsApi/v2/get-organisations-basic?${queryParams.toString()}`
    )
    .then((response) => response.data)
    .catch((err) => {
      console.log(`error get-organisations-basic : ${err}`);
      return null;
    });

  setCache(cacheKey, res);
  return res;
};

const fetchPaginatedVisibleRecords = async ({
  fetchPage,
  getRawRows,
  pageSize = 500,
  maxPages = 200,
}) => {
  const aggregated = [];
  let page = 1;

  while (page <= maxPages) {
    const response = await fetchPage(page, pageSize);
    const rawRows = getRawRows(response);

    if (!Array.isArray(rawRows) || rawRows.length === 0) {
      break;
    }

    aggregated.push(...excludeDeletedUsers(rawRows));

    if (rawRows.length < pageSize) {
      break;
    }

    page += 1;
  }

  return aggregated;
};

export const fetchAllVisibleUsersBasic = async (params = {}) => {
  return fetchPaginatedVisibleRecords({
    pageSize: params.fetchPageSize ?? 500,
    fetchPage: (page, limit) => getUsersBasic({ ...params, page, limit }),
    getRawRows: (response) => response?.data ?? [],
  });
};

export const fetchAllOrganisationsBasic = async (params = {}) => {
  return fetchPaginatedVisibleRecords({
    pageSize: params.fetchPageSize ?? 500,
    fetchPage: (page, limit) => getOrganisationsBasic({ ...params, page, limit }),
    getRawRows: (response) => response?.data ?? [],
  });
};

export const fetchAllVisibleDashboardUsers = async (params = {}) => {
  const aggregated = [];
  let page = 1;
  const pageSize = params.fetchPageSize ?? 500;
  const maxPages = 200;

  while (page <= maxPages) {
    const batch = await fetchDashboardTotalUsers({ ...params, page, limit: pageSize });

    if (!Array.isArray(batch) || batch.length === 0) {
      break;
    }

    const userRows = batch.filter((user) => typeof user === "object" && user?.userId);
    aggregated.push(...excludeDeletedUsers(userRows));

    if (userRows.length < pageSize) {
      break;
    }

    page += 1;
  }

  return aggregated;
};

export const fetchCarpoolEvents = (params) => {
  // Pas de cache ici car accès Firestore direct (peut être ajouté si besoin)
  return firestore
    .collection(params.role === 'city' ? COLLECTION.Cities : COLLECTION.Organisations)
    .doc(params.ownerId)
    .collection(COLLECTION.carpoolEvents)
    .get()
    .then(firestoreToArray);
}

export const fetchCarpoolEventsStream = (params) => {
  // Pas de cache ici, stream Firestore
  return firestore
    .collection(params.role === 'city' ? COLLECTION.Cities : COLLECTION.Organisations)
    .doc(params.ownerId)
    .collection(COLLECTION.carpoolEvents);
}

export const fetchSingleCarpoolEvent = (params) => {
  // Pas de cache ici, accès Firestore direct
  return firestore
    .collection(params.role === 'city' ? COLLECTION.Cities : COLLECTION.Organisations)
    .doc(params.ownerId)
    .collection(COLLECTION.carpoolEvents)
    .doc(params.eventId)
    .get()
    .then(doc => ({ ...doc.data(), id: doc.id }));
}

export const getSingleCarpoolEventRef = (params) => {
  // Pas de cache ici, retourne une référence Firestore
  const base = firestore
    .collection(params.role === 'city' ? COLLECTION.Cities : COLLECTION.Organisations)
    .doc(params.ownerId)
    .collection(COLLECTION.carpoolEvents);

  return params.eventId ? base.doc(params.eventId) : base.doc();
}

export const createCarpoolEventApi = async (params) => {
  const cacheKey = getCacheKey('createCarpoolEventApi', params);
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const res = await axios
    .post(
      `${VITE_CLOUD_FUNCTION_API_URL}/carpoolApi/create-carpool-event/${params.ownerId}/${params.eventId}/${params.role}`,
      {}
    )
    .then((res) => res.data)
    .catch((err) => { console.log(`error create-carpool-event : ${err}`) });

  setCache(cacheKey, res);
  return res;
};
